import {
  Component,
  h,
  Prop,
  Event,
  EventEmitter,
  Watch,
  Listen,
  Method,
} from "@stencil/core";
import { InputType, TextFieldProps } from "./f-text-field-model";
import { generateRandomNumber } from "@globals/random-number-helpers";

@Component({
  tag: "f-text-field",
  styleUrl: "f-text-field.scss",
  shadow: true,
})
export class FTextField {
  private input: HTMLInputElement;

  @Prop() propertyId: TextFieldProps["propertyId"] =
    "f-text-field-" + generateRandomNumber();
  @Prop() name: TextFieldProps["name"];
  @Prop() placeholder: TextFieldProps["placeholder"];
  @Prop() label: TextFieldProps["label"];
  @Prop() hiddenLabel: TextFieldProps["hiddenLabel"] = false;
  @Prop() floatingLabel: TextFieldProps["floatingLabel"] = true;
  @Prop() legend: TextFieldProps["legend"];
  @Prop() maxLength: TextFieldProps["maxLength"] = 255;
  @Prop() isValid: TextFieldProps["isValid"];
  @Prop() type: TextFieldProps["type"] = "text";
  @Prop() min: TextFieldProps["min"];
  @Prop() max: TextFieldProps["max"];
  @Prop() step: TextFieldProps["step"];
  @Prop() inputChangeHandler: TextFieldProps["inputChangeHandler"];
  @Prop() additionalProps: TextFieldProps["additionalProps"];
  @Prop() inputTitle: TextFieldProps["inputTitle"];
  @Prop({ mutable: true }) mandatory: TextFieldProps["mandatory"] = false;
  @Prop({ mutable: true }) disabled: TextFieldProps["disabled"] = false;

  @Prop({ mutable: true }) value: TextFieldProps["value"];

  @Watch("value")
  watchValueChanges(newValue: string, oldValue: string): void {
    this.textFieldChanged.emit({
      fieldId: this.propertyId,
      oldValue: oldValue,
      newValue: newValue,
      valid:
        this.isValid !== null && this.isValid !== undefined
          ? this.isValid()
          : true,
    });
  }

  @Event({ eventName: "textFieldChanged" }) textFieldChanged: EventEmitter<{
    fieldId: string;
    oldValue: string;
    newValue: string;
    valid: boolean;
  }>;

  @Listen("triggerFocusOnTextField", { target: "window" })
  onTriggerFocusOnTextField(event: CustomEvent): void {
    if (event.detail.fieldId === this.propertyId) {
      this.input.focus();
    }
  }

  @Method()
  async focusOnTextField(): Promise<void> {
    const end = this.input.value.length;
    this.input.setSelectionRange(end, end);

    window.setTimeout(() => {
      this.input.focus();
    }, 0);
  }

  private handleInput = (event: InputEvent): void => {
    const value = (event.target as HTMLInputElement).value;

    if (this.type === InputType.NUMBER) {
      const truncatedInputValue = value.replace(/[^0-9]/g, "");

      this.input.value = truncatedInputValue;
      this.value = truncatedInputValue;
    } else {
      this.value = value;
    }
  };

  render() {
    return (
      <div
        class={{
          "a-text-field": true,
          "a-text-field--floating-label":
            Boolean(this.label) &&
            !Boolean(this.hiddenLabel) &&
            Boolean(this.floatingLabel),
          "a-text-field--disabled": this.disabled,
        }}
        data-test="text-field"
      >
        {Boolean(this.label) ? (
          <label
            htmlFor={this.propertyId}
            class={
              Boolean(this.hiddenLabel)
                ? "a-text-field__label acc-hide"
                : "a-text-field__label"
            }
            data-test="text-field-label"
          >
            {this.label} {this.mandatory ? "*" : ""}
          </label>
        ) : (
          ""
        )}
        <input
          type={this.type === InputType.NUMBER ? InputType.TEXT : this.type}
          inputmode={
            this.type === InputType.NUMBER ? "numeric" : InputType.TEXT // when porting to 9.5 use TextFieldMode
          }
          name={this.name}
          onInput={this.handleInput}
          onChange={this.inputChangeHandler}
          id={this.propertyId}
          class="a-text-field__input"
          maxlength={this.maxLength}
          disabled={this.disabled}
          placeholder={this.placeholder}
          min={this.min}
          max={this.max}
          step={this.step}
          value={this.value}
          ref={(el) => (this.input = el)}
          title={this.inputTitle}
          data-test="text-field-input"
        />
      </div>
    );
  }
}
